import React, { useContext, useState, FormEvent, ChangeEvent } from "react";
import {
  Alert,
  Button,
  FormHelperText,
  Grid,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useTextDomainContext } from "@/app/providers";
import { useForgotPassword } from "@/hooks";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

interface ForgotPasswordPayload {
  email: string;
}

/**
 * Forgot Password Form Component
 */
export const ForgotPasswordForm = (): React.ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const [email, setEmail] = useState<string>("");
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const [emailError, setEmailError] = useState<boolean>(false);

  const { submit, isSubmitting } = useForgotPassword(
    () => {
      setErrorMessage([]);
      setInfoMessage([
        gettext(
          "A link has been sent to your email address to reset password.",
        ),
      ]);
      setEmailSent(true);
    },
    (error: string) => {
      setInfoMessage([]);
      setErrorMessage([error]);
    },
  );

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setEmail(event.target.value);
    if (event.target.value) {
      setEmailError(false);
    } else {
      setEmailError(true);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setErrorMessage([]);
    setInfoMessage([]);
    setInfoMessage([gettext("Validating. Please wait.")]);
    const payload: ForgotPasswordPayload = {
      email,
    };
    submit(payload);
  };

  return (
    <>
      {!emailSent && (
        <Grid container spacing={1} sx={{ width: "100%" }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid
              container
              spacing={2}
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              <Grid size={{ xs: 12, sm: 8 }} sx={{ textAlign: "left" }}>
                <Typography variant="h2" sx={{ color: "grey.900" }}>
                  {gettext("Forgot Password")}
                </Typography>
                <Grid size={{ xs: 12, sm: 8 }} sx={{ mb: 2 }}>
                  <Typography variant="body2" align="left">
                    {gettext("Enter email address to continue.")}
                  </Typography>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <Stack>
                  <OutlinedInput
                    fullWidth
                    size="medium"
                    id="email-signup"
                    type="email"
                    placeholder={gettext("Enter email address")}
                    value={email}
                    name="email"
                    onChange={handleEmailChange}
                    inputProps={{}}
                    required
                  />
                </Stack>
                {emailError && (
                  <FormHelperText error id="accountId-error">
                    {gettext("Email is required.")}
                  </FormHelperText>
                )}
              </Grid>

              <Grid size={{ xs: 12, sm: 8 }}>
                <Button
                  disableElevation
                  disabled={isSubmitting || !email}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  sx={{ color: "primary" }}
                >
                  {gettext("Send Link to Email")}
                </Button>
              </Grid>

              {infoMessage.length > 0 && (
                <Grid size={{ xs: 12, sm: 8 }}>
                  <Alert
                    severity="info"
                    sx={{ whiteSpace: "pre-line", textAlign: "left" }}
                  >
                    {infoMessage.join("\n")}
                  </Alert>
                </Grid>
              )}
              {errorMessage.length > 0 && (
                <Grid size={{ xs: 12, sm: 8 }}>
                  <Alert
                    severity="error"
                    sx={{ whiteSpace: "pre-line", textAlign: "left" }}
                  >
                    {errorMessage.join("\n")}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
      )}

      {emailSent && (
        <Box>
          <Grid
            container
            spacing={2}
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <Grid size={{ xs: 12, sm: 8 }} sx={{ textAlign: "left" }}>
              <TaskAltIcon color="success" sx={{ fontSize: 60 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Typography variant="h2" align="left" sx={{ color: "grey.900" }}>
                {gettext("Email Sent ")}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }} sx={{ mb: 5 }}>
              <Typography variant="body2" align="left">
                {gettext(
                  `Check your email ${email}, we have provided a link to reset your password`,
                )}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};
